import { DESIGN_REMIX_PROCESS_TYPE, DESIGN_REMIX_STATUS } from 'utlis/constants';

// Higher rank wins when the same process shows up more than once
const STATUS_RANK = {
  [DESIGN_REMIX_STATUS.COMPLETED]: 3,
  [DESIGN_REMIX_STATUS.FAILED]: 2,
  [DESIGN_REMIX_STATUS.IN_PROGRESS]: 1,
};

const DISMISS_FIELDS = ['is_dismissed', 'dismissed_at', 'dismissed_by'];

const getRank = entry => STATUS_RANK[entry?.status] ?? 0;

function mergeDuplicateRefinements(entries) {
  const ascending = [...entries].sort((a, b) => getRank(a) - getRank(b));
  const merged = {};

  // Later (higher ranked) entries override earlier ones, empty values never override
  ascending.forEach(entry => {
    Object.entries(entry).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        merged[key] = value;
      }
    });
  });

  // The backend applies dismiss / put-back to the FIRST entry with a matching process_uuid, and a put-back
  // clears the timestamp. So the first entry that carries a dismiss state is the one the user last acted on;
  const dismissSource = entries.find(entry => entry.is_dismissed !== undefined && entry.is_dismissed !== null);

  if (dismissSource) {
    DISMISS_FIELDS.forEach(field => {
      merged[field] = dismissSource[field];
    });
  }

  return merged;
}

/**
 * The same refinement (process_uuid) can be present more than once in a stored
 * remix preview, e.g. a stale in-progress entry next to its completed entry.
 * Collapse them into one entry so a variant is never rendered twice.
 */
export const dedupeRefinements = refinements => {
  if (!Array.isArray(refinements)) {
    return refinements;
  }

  const groups = new Map();
  const result = [];
  let hasDuplicates = false;

  refinements.forEach(refinement => {
    const processUuid = refinement?.process_uuid;

    if (!processUuid) {
      result.push(refinement);

      return;
    }

    if (!groups.has(processUuid)) {
      groups.set(processUuid, { index: result.length, entries: [] });
      result.push(null);
    }
    else {
      hasDuplicates = true;
    }

    groups.get(processUuid).entries.push(refinement);
  });

  // Nothing to merge: keep the original array so unaffected audits behave exactly as before
  if (!hasDuplicates) {
    return refinements;
  }

  groups.forEach(({ index, entries }) => {
    result[index] = entries.length === 1 ? entries[0] : mergeDuplicateRefinements(entries);
  });

  return result;
};

export const normalizeRemixPreview = preview => {
  if (!preview || !Array.isArray(preview.refinements)) {
    return preview;
  }

  const refinements = dedupeRefinements(preview.refinements);

  return refinements === preview.refinements ? preview : { ...preview, refinements };
};

const isPrototypeEntryFor = (entry, inputReference) => (
  entry?.process_type === DESIGN_REMIX_PROCESS_TYPE.PROTOTYPE
  && String(entry?.input_reference) === String(inputReference)
);

/**
 * Variant state built by the backend from post-process rows (`orm_post_processes`). Unlike the
 * copies in extra_data.post_processes it is never overwritten by concurrent writers, so prefer it.
 * Returns null when the record has no entry for this insight uuid / page key.
 */
export const findOrmRemixPreview = (record, inputReference) => {
  const entries = record?.orm_post_processes;

  if (!Array.isArray(entries) || inputReference == null) {
    return null;
  }

  const entry = entries.find(item => isPrototypeEntryFor(item, inputReference));

  if (!entry?.remix_preview) {
    return null;
  }

  // Failed rows carry their error on the entry itself, the panels read it from the preview
  if (!entry.remix_preview.error_message && entry.error_message) {
    return { ...entry.remix_preview, error_message: entry.error_message };
  }

  return entry.remix_preview;
};

// Mirrors local remix changes into orm_post_processes so the sync effects don't read stale state
export const upsertOrmRemixPreview = (entries, inputReference, remixPreview, inputPayload) => {
  const current = Array.isArray(entries) ? entries : [];
  const index = current.findIndex(item => isPrototypeEntryFor(item, inputReference));
  const existing = index > -1 ? current[index] : null;

  const nextEntry = {
    ...(existing || {
      process_type: DESIGN_REMIX_PROCESS_TYPE.PROTOTYPE,
      input_reference: inputReference,
      input_payload: inputPayload,
    }),
    uuid: remixPreview?.process_uuid ?? existing?.uuid ?? null,
    // A retry must not inherit the previous failure
    error_message: remixPreview?.status === DESIGN_REMIX_STATUS.FAILED
      ? (remixPreview.error_message ?? existing?.error_message ?? null)
      : null,
    remix_preview: remixPreview,
  };

  if (index === -1) {
    return [...current, nextEntry];
  }

  const next = [...current];

  next[index] = nextEntry;

  return next;
};

// Panel keys must be unique even if two refinements share a version (concurrent refines by two users)
export const buildUniquePanelKey = (baseKey, processUuid, usedKeys) => {
  const key = usedKeys.has(baseKey) && processUuid ? `${baseKey}-${processUuid}` : baseKey;

  usedKeys.add(key);

  return key;
};
